// Settings — Appearance / A11y / Notifications / Users / Retention
// Wires a11y toggles to the live DOM via useSettingsStore and additional flags.

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSettingsStore } from "@/store/settings";
import { useToast } from "@/components/primitives/Toast";
import type { CSSProperties } from "react";

type Tab = "appearance" | "a11y" | "notifications" | "users" | "retention" | "about";
const TABS: { id: Tab; label: string }[] = [
  { id: "appearance", label: "Appearance" },
  { id: "a11y", label: "Accessibility" },
  { id: "notifications", label: "Notifications" },
  { id: "users", label: "Users & Roles" },
  { id: "retention", label: "Data Retention" },
  { id: "about", label: "About" },
];

// Extra a11y prefs (beyond useSettingsStore) — persisted via localStorage and
// applied to <html> data attributes / body classes.
interface ExtraA11y {
  focusStrength: "subtle" | "strong" | "maximum";
  dyslexiaFont: boolean;
  srAnnouncements: boolean;
}
const EXTRA_KEY = "lexicon.a11y.extra";
function loadExtra(): ExtraA11y {
  try { return JSON.parse(localStorage.getItem(EXTRA_KEY) || "") || defaults(); } catch { return defaults(); }
}
function defaults(): ExtraA11y { return { focusStrength: "strong", dyslexiaFont: false, srAnnouncements: true }; }

function applyExtra(e: ExtraA11y) {
  const root = document.documentElement;
  root.dataset.focus = e.focusStrength;
  if (e.dyslexiaFont) root.classList.add("dyslexia-font"); else root.classList.remove("dyslexia-font");
  root.dataset.srLive = e.srAnnouncements ? "on" : "off";
  // Inject CSS shims once
  if (!document.getElementById("lex-a11y-shim")) {
    const style = document.createElement("style");
    style.id = "lex-a11y-shim";
    style.textContent = `
      :root[data-focus="subtle"] :focus-visible { outline-width: 1px; }
      :root[data-focus="strong"] :focus-visible { outline-width: 2px; }
      :root[data-focus="maximum"] :focus-visible { outline-width: 4px; outline-offset: 3px; }
      :root[data-contrast="high"] { --md-outline-var: var(--md-on-surface); --md-on-surface-var: var(--md-on-surface); }
      :root[data-contrast="high"] body { background: var(--md-sc-lowest); }
      html.dyslexia-font, html.dyslexia-font body { font-family: "OpenDyslexic", "Comic Sans MS", "Geist", sans-serif !important; }
      @media (prefers-reduced-motion: reduce) { * { transition: none !important; animation: none !important; } }
    `;
    document.head.appendChild(style);
  }
}

export function Settings() {
  const params = useParams();
  const nav = useNavigate();
  const tab = (TABS.find(t => t.id === params.tab)?.id || "appearance") as Tab;
  const s = useSettingsStore();
  const [extra, setExtra] = useState<ExtraA11y>(loadExtra);
  const toast = useToast();
  const [notifPrefs, setNotifPrefs] = useState<Record<string, { app: boolean; email: boolean }>>(() => {
    try { return JSON.parse(localStorage.getItem("lexicon.notif") || "") || {}; } catch { return {}; }
  });
  const [retention, setRetention] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem("lexicon.retention") || "") || { version: "365", audit: "2555", soft: "30" }; } catch { return { version: "365", audit: "2555", soft: "30" }; }
  });
  const persistNotif = (next: typeof notifPrefs) => { setNotifPrefs(next); localStorage.setItem("lexicon.notif", JSON.stringify(next)); toast("Saved", "ok"); };
  const persistRetention = (k: string, v: string) => { const next = { ...retention, [k]: v }; setRetention(next); localStorage.setItem("lexicon.retention", JSON.stringify(next)); toast("Saved", "ok"); };

  useEffect(() => { applyExtra(extra); localStorage.setItem(EXTRA_KEY, JSON.stringify(extra)); }, [extra]);
  useEffect(() => { s.applyToDocument(); }, [s.theme, s.density, s.fontScale, s.highContrast, s.reduceMotion]);

  const [announce, setAnnounce] = useState("");
  const fire = (msg: string) => { if (extra.srAnnouncements) { setAnnounce(""); setTimeout(() => setAnnounce(msg), 30); } };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 16, padding: 16 }}>
      <aside style={{ ...panel, padding: 8, height: "fit-content" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => nav(`/settings/${t.id}`)} style={{
            display: "block", width: "100%", textAlign: "left", padding: "10px 14px",
            border: "none", cursor: "pointer", borderRadius: "var(--r-md)",
            background: tab === t.id ? "var(--md-secondary-container)" : "transparent",
            color: tab === t.id ? "var(--md-on-secondary-container)" : "var(--md-on-surface)",
            font: "var(--t-title-sm)",
          }}>{t.label}</button>
        ))}
        <a href="/audit" style={{ display: "block", padding: "10px 14px", color: "var(--md-tertiary)", textDecoration: "none", font: "var(--t-body-sm)", marginTop: 8 }}>Audit log →</a>
      </aside>

      <section style={{ ...panel, padding: 28 }}>
        <div style={eyebrow}>SETTINGS</div>
        <div style={{ font: "var(--t-headline-md)", fontStyle: "italic", fontWeight: 400, marginBottom: 20 }}>
          {TABS.find(t => t.id === tab)!.label}
        </div>

        {tab === "appearance" && (
          <div style={{ display: "grid", gap: 22, maxWidth: 640 }}>
            <Row label="Theme" hint="Light, dark, or follow system.">
              <Segmented value={s.theme} options={[{ v: "light" }, { v: "dark" }, { v: "system" }]} onChange={v => { s.setTheme(v as any); fire(`Theme set to ${v}`); }} />
            </Row>
            <Row label="Density" hint="Row height across tables.">
              <Segmented value={s.density} options={[{ v: "compact" }, { v: "cozy" }, { v: "comfortable" }]} onChange={v => { s.setDensity(v as any); fire(`Density: ${v}`); }} />
            </Row>
            <Row label="Font size baseline" hint="Affects all body and heading sizes.">
              <Segmented value={s.fontScale} options={[{ v: "md", l: "Small" }, { v: "lg", l: "Medium" }, { v: "xl", l: "Large" }]} onChange={v => { s.setFontScale(v as any); fire("Font scale changed"); }} />
            </Row>
            <Row label="Accent preview">
              <div style={{ display: "flex", gap: 8 }}>
                <span style={{ width: 36, height: 36, borderRadius: "var(--shape-pebble)", background: "var(--md-primary)" }} />
                <span style={{ width: 36, height: 36, borderRadius: "var(--shape-pebble)", background: "var(--md-secondary)" }} />
                <span style={{ width: 36, height: 36, borderRadius: "var(--shape-pebble)", background: "var(--md-tertiary)" }} />
              </div>
            </Row>
          </div>
        )}

        {tab === "a11y" && (
          <div style={{ display: "grid", gap: 18, maxWidth: 640 }}>
            <Row label="High contrast" hint="Pure black/white text, stronger outlines.">
              <Switch checked={s.highContrast} onChange={v => { s.setHighContrast(v); fire(`High contrast ${v ? "on" : "off"}`); }} />
            </Row>
            <Row label="Reduced motion" hint="Disables transitions and animations.">
              <Switch checked={s.reduceMotion} onChange={v => { s.setReduceMotion(v); fire(`Motion reduced: ${v}`); }} />
            </Row>
            <Row label="Focus indicator strength" hint="Visible outline width on keyboard focus.">
              <Segmented value={extra.focusStrength} options={[{ v: "subtle" }, { v: "strong" }, { v: "maximum" }]} onChange={v => setExtra({ ...extra, focusStrength: v as any })} />
            </Row>
            <Row label="Screen-reader announcements" hint="Speak save/approve/reject actions via aria-live.">
              <Switch checked={extra.srAnnouncements} onChange={v => setExtra({ ...extra, srAnnouncements: v })} />
            </Row>
            <Row label="Dyslexia-friendly font" hint="Switches to OpenDyslexic family.">
              <Switch checked={extra.dyslexiaFont} onChange={v => setExtra({ ...extra, dyslexiaFont: v })} />
            </Row>
            <Row label="Keyboard shortcuts">
              <table style={{ font: "var(--t-body-sm)", borderCollapse: "collapse" }}>
                <tbody>
                  {[["⌘ K", "Open command palette"], ["G H", "Home"], ["G B", "Browse"], ["G A", "Approvals"], ["?", "Show shortcuts"]].map(([k, l]) => (
                    <tr key={k}><td style={{ padding: "4px 8px", font: "var(--t-mono-sm)" }}>{k}</td><td style={{ padding: "4px 8px", color: "var(--md-on-surface-var)" }}>{l}</td></tr>
                  ))}
                </tbody>
              </table>
            </Row>
            <Row label="Skip to content"><Switch checked={true} onChange={v => toast(`Skip-to-content ${v ? "enabled" : "disabled"}`, "ok")} /></Row>
            <div aria-live="polite" aria-atomic="true" style={{ position: "absolute", left: -9999 }}>{announce}</div>
          </div>
        )}

        {tab === "notifications" && (
          <div style={{ maxWidth: 720 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Event</th><th style={th}>In-app</th><th style={th}>Email</th></tr></thead>
              <tbody>
                {["Approvals pending", "Quality rule violations", "Import completed", "Sync errors", "Webhook failures"].map((row, i) => {
                  const current = notifPrefs[row] ?? { app: i !== 4, email: i < 2 };
                  return (
                    <tr key={row} style={{ borderTop: "1px solid var(--md-outline-var)" }}>
                      <td style={td}>{row}</td>
                      <td style={td}><Switch checked={current.app} onChange={v => persistNotif({ ...notifPrefs, [row]: { ...current, app: v } })} /></td>
                      <td style={td}><Switch checked={current.email} onChange={v => persistNotif({ ...notifPrefs, [row]: { ...current, email: v } })} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "users" && (
          <div style={{ display: "grid", gap: 20 }}>
            <div>
              <div style={{ font: "var(--t-title-md)", marginBottom: 8 }}>Members</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Name</th><th style={th}>Email</th><th style={th}>Role</th><th style={th}>Last seen</th></tr></thead>
                <tbody>
                  {[
                    ["Olivia Tan", "olivia.t@example.com", "admin"],
                    ["Marcus Jin", "marcus.j@example.com", "steward"],
                    ["Priya Shah", "priya.s@example.com", "contributor"],
                    ["Ben Wood", "ben.w@example.com", "viewer"],
                    ["Audit Bot", "audit@example.com", "auditor"],
                  ].map(([name, email, role]) => (
                    <tr key={email} style={{ borderTop: "1px solid var(--md-outline-var)" }}>
                      <td style={td}>{name}</td>
                      <td style={{ ...td, font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{email}</td>
                      <td style={td}><span style={chipSm}>{role}</span></td>
                      <td style={{ ...td, font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>just now</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <div style={{ font: "var(--t-title-md)", marginBottom: 8 }}>Permissions matrix</div>
              <table style={{ width: "100%", borderCollapse: "collapse", font: "var(--t-body-sm)" }}>
                <thead><tr><th style={th}>Capability</th><th style={th}>Admin</th><th style={th}>Steward</th><th style={th}>Contrib</th><th style={th}>Viewer</th><th style={th}>Auditor</th></tr></thead>
                <tbody>
                  {[
                    ["Read records", 1, 1, 1, 1, 1],
                    ["Propose changes", 1, 1, 1, 0, 0],
                    ["Approve", 1, 1, 0, 0, 0],
                    ["Manage API keys", 1, 0, 0, 0, 0],
                    ["View audit log", 1, 1, 0, 0, 1],
                  ].map(([cap, ...vals]) => (
                    <tr key={cap as string} style={{ borderTop: "1px solid var(--md-outline-var)" }}>
                      <td style={td}>{cap as string}</td>
                      {vals.map((v, i) => <td key={i} style={{ ...td, textAlign: "center", color: v ? "var(--forest-500)" : "var(--md-on-surface-var)" }}>{v ? "●" : "—"}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "retention" && (
          <div style={{ display: "grid", gap: 18, maxWidth: 640 }}>
            <Row label="Version retention" hint="Per-domain history retained.">
              <Segmented value={retention.version} options={[{ v: "30", l: "30 days" }, { v: "180", l: "6 months" }, { v: "365", l: "1 year" }, { v: "0", l: "Forever" }]} onChange={v => persistRetention("version", v)} />
            </Row>
            <Row label="Audit log retention" hint="Required for compliance.">
              <Segmented value={retention.audit} options={[{ v: "365", l: "1 year" }, { v: "1825", l: "5 years" }, { v: "2555", l: "7 years" }]} onChange={v => persistRetention("audit", v)} />
            </Row>
            <Row label="Soft-delete grace period" hint="Records recoverable for…">
              <Segmented value={retention.soft} options={[{ v: "7", l: "1 week" }, { v: "30", l: "30 days" }, { v: "90", l: "90 days" }]} onChange={v => persistRetention("soft", v)} />
            </Row>
          </div>
        )}

        {tab === "about" && (
          <div style={{ color: "var(--md-on-surface-var)" }}>
            <p>AIDAN — Automated Index for Data Asset Nomenclature</p>
            <p>Build 2026.05.15 · v0.9.0-rc1</p>
            <p>© Davicity Pty Ltd</p>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 24, alignItems: "center" }}>
      <div>
        <div style={{ font: "var(--t-title-sm)" }}>{label}</div>
        {hint && <div style={{ font: "var(--t-body-sm)", color: "var(--md-on-surface-var)", marginTop: 2 }}>{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Segmented({ value, options, onChange }: { value: string; options: { v: string; l?: string }[]; onChange: (v: string) => void }) {
  return (
    <div style={{ display: "inline-flex", padding: 4, background: "var(--md-sc-low)", borderRadius: "var(--r-pill)" }}>
      {options.map(o => (
        <button key={o.v} onClick={() => onChange(o.v)} style={{
          padding: "6px 14px", border: "none", cursor: "pointer", borderRadius: "var(--r-pill)",
          background: value === o.v ? "var(--md-primary)" : "transparent",
          color: value === o.v ? "var(--md-on-primary)" : "var(--md-on-surface)",
          font: "var(--t-label-md)", textTransform: "capitalize",
        }}>{o.l ?? o.v}</button>
      ))}
    </div>
  );
}

function Switch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!checked)} aria-pressed={checked} style={{
      width: 40, height: 22, borderRadius: 999, border: "none", cursor: "pointer", padding: 2,
      background: checked ? "var(--md-primary)" : "var(--md-outline-var)",
      display: "flex", alignItems: "center", justifyContent: checked ? "flex-end" : "flex-start",
    }}>
      <span style={{ width: 18, height: 18, borderRadius: 999, background: "var(--md-sc-lowest)" }} />
    </button>
  );
}

const panel: CSSProperties = { background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-lg)" };
const eyebrow: CSSProperties = { font: "var(--t-label-sm)", color: "var(--md-tertiary)", letterSpacing: "0.12em" };
const th: CSSProperties = { textAlign: "left", padding: "10px 14px", font: "var(--t-label-md)", color: "var(--md-on-surface-var)" };
const td: CSSProperties = { padding: "10px 14px", font: "var(--t-body-md)" };
const chipSm: CSSProperties = { padding: "2px 10px", borderRadius: "var(--r-pill)", background: "var(--md-secondary-container)", color: "var(--md-on-secondary-container)", font: "var(--t-label-sm)" };

export default Settings;
